// src/components/translation-modules/MenuManager.js
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaArrowUp,
  FaArrowDown,
  FaBoxOpen,
  FaUtensils,
  FaImage,
  FaExternalLinkAlt,
} from "react-icons/fa";
import TranslationModuleBase from "./TranslationModuleBase";

const MenuManager = ({
  currentTranslations,
  otherTranslations,
  currentLang,
  secondaryLang,
  onUpdateTranslations,
  page = "menuCarta",
  onSectionChange,
  onModuleChange,
}) => {
  const [menuSections, setMenuSections] = useState({});
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [showAddSectionForm, setShowAddSectionForm] = useState(false);
  const [newSectionKey, setNewSectionKey] = useState("");
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [newItemData, setNewItemData] = useState({
    key: "",
    name: "",
    desc: "",
    price: "",
    allergens: [],
  });
  const [allergens, setAllergens] = useState({});

  // Extract menu data from translations
  useEffect(() => {
    if (
      currentTranslations &&
      currentTranslations[page] &&
      currentTranslations[page].sections
    ) {
      setMenuSections(currentTranslations[page].sections);
    }

    // Initialize collapsed state
    if (
      currentTranslations &&
      currentTranslations[page] &&
      currentTranslations[page].sections
    ) {
      const sections = currentTranslations[page].sections;
      const initCollapsed = {};
      Object.keys(sections).forEach((sectionKey) => {
        initCollapsed[sectionKey] = true;
      });
      setCollapsedSections(initCollapsed);
    }

    // Get allergens
    if (
      currentTranslations &&
      currentTranslations.menuCarta &&
      currentTranslations.menuCarta.allergens &&
      currentTranslations.menuCarta.allergens.items
    ) {
      setAllergens(currentTranslations.menuCarta.allergens.items);
    }
  }, [currentTranslations, page]);

  const toggleSectionCollapse = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination) return;

    // If dragging sections
    if (type === "section") {
      const sectionKeys = Object.keys(menuSections);
      const [movedSectionKey] = sectionKeys.splice(source.index, 1);
      sectionKeys.splice(destination.index, 0, movedSectionKey);

      const reorderedSections = {};
      sectionKeys.forEach((key) => {
        reorderedSections[key] = menuSections[key];
      });

      setMenuSections(reorderedSections);
      saveMenuSections(reorderedSections);
    }
    // If dragging items within a section
    else if (type === "item") {
      const sectionKey = source.droppableId;
      const sectionItems = menuSections[sectionKey].items;

      const itemKeys = Object.keys(sectionItems);
      const [movedItemKey] = itemKeys.splice(source.index, 1);
      itemKeys.splice(destination.index, 0, movedItemKey);

      const reorderedItems = {};
      itemKeys.forEach((key) => {
        reorderedItems[key] = sectionItems[key];
      });

      const updatedSections = {
        ...menuSections,
        [sectionKey]: {
          ...menuSections[sectionKey],
          items: reorderedItems,
        },
      };

      setMenuSections(updatedSections);
      saveMenuSections(updatedSections);
    }
  };

  const saveMenuSections = (updatedSections) => {
    // Update current language
    const updatedCurrentTranslations = {
      ...currentTranslations,
      [page]: {
        ...currentTranslations[page],
        sections: updatedSections,
      },
    };

    // Make sure the other language has the same structure
    let updatedOtherTranslations = { ...otherTranslations };

    if (!updatedOtherTranslations[page]) {
      updatedOtherTranslations[page] = {};
    }

    if (!updatedOtherTranslations[page].sections) {
      updatedOtherTranslations[page].sections = {};
    }

    // Ensure each section exists in the other language
    Object.keys(updatedSections).forEach((sectionKey) => {
      if (!updatedOtherTranslations[page].sections[sectionKey]) {
        updatedOtherTranslations[page].sections[sectionKey] = {
          title: updatedSections[sectionKey].title,
          items: {},
        };
      }

      // Ensure items have the same structure and order
      const currentItems = updatedSections[sectionKey].items || {};
      const otherItems =
        updatedOtherTranslations[page].sections[sectionKey].items || {};

      const syncedItems = {};
      Object.keys(currentItems).forEach((itemKey) => {
        syncedItems[itemKey] = otherItems[itemKey] || {
          ...currentItems[itemKey],
        };
      });

      updatedOtherTranslations[page].sections[sectionKey].items = syncedItems;
    });

    onUpdateTranslations(updatedCurrentTranslations, updatedOtherTranslations);
  };

  const handleEditSection = (sectionKey) => {
    setCurrentSection({
      key: sectionKey,
      title: menuSections[sectionKey].title,
    });
    setIsEditingSection(true);
  };

  const handleUpdateSection = () => {
    if (!currentSection) return;

    const updatedSections = {
      ...menuSections,
      [currentSection.key]: {
        ...menuSections[currentSection.key],
        title: currentSection.title,
      },
    };

    setMenuSections(updatedSections);
    saveMenuSections(updatedSections);
    setIsEditingSection(false);
    setCurrentSection(null);
  };

  const handleDeleteSection = (sectionKey) => {
    setConfirmDeleteSection(sectionKey);
  };

  const confirmDeleteSectionAction = () => {
    if (!confirmDeleteSection) return;

    const { [confirmDeleteSection]: _, ...remainingSections } = menuSections;
    setMenuSections(remainingSections);
    saveMenuSections(remainingSections);
    setConfirmDeleteSection(null);
  };

  const handleAddSection = () => {
    setNewSectionKey("");
    setNewSectionTitle("");
    setShowAddSectionForm(true);
  };

  const saveNewSection = () => {
    if (!newSectionKey || !newSectionTitle) {
      alert("Section key and title are required.");
      return;
    }

    // Check if key already exists
    if (menuSections[newSectionKey]) {
      alert("This section key already exists. Please choose a different key.");
      return;
    }

    const updatedSections = {
      ...menuSections,
      [newSectionKey]: {
        title: newSectionTitle,
        items: {},
      },
    };

    setMenuSections(updatedSections);
    saveMenuSections(updatedSections);
    setShowAddSectionForm(false);
  };

  const handleEditItem = (sectionKey, itemKey) => {
    setCurrentItem({
      sectionKey,
      key: itemKey,
      ...menuSections[sectionKey].items[itemKey],
    });
    setIsEditingItem(true);
  };

  const handleUpdateItem = () => {
    if (!currentItem) return;

    const { sectionKey, key, ...itemData } = currentItem;

    const updatedSections = {
      ...menuSections,
      [sectionKey]: {
        ...menuSections[sectionKey],
        items: {
          ...menuSections[sectionKey].items,
          [key]: itemData,
        },
      },
    };

    setMenuSections(updatedSections);
    saveMenuSections(updatedSections);
    setIsEditingItem(false);
    setCurrentItem(null);
  };

  const handleDeleteItem = (sectionKey, itemKey) => {
    setConfirmDeleteItem({ sectionKey, itemKey });
  };

  const confirmDeleteItemAction = () => {
    if (!confirmDeleteItem) return;

    const { sectionKey, itemKey } = confirmDeleteItem;

    const { [itemKey]: _, ...remainingItems } = menuSections[sectionKey].items;

    const updatedSections = {
      ...menuSections,
      [sectionKey]: {
        ...menuSections[sectionKey],
        items: remainingItems,
      },
    };

    setMenuSections(updatedSections);
    saveMenuSections(updatedSections);
    setConfirmDeleteItem(null);
  };

  const handleAddItem = (sectionKey) => {
    setNewItemData({
      key: "",
      name: "",
      desc: "",
      price: "",
      allergens: [],
    });
    setShowAddItemForm(sectionKey);
  };

  const saveNewItem = () => {
    if (!newItemData.key || !newItemData.name) {
      alert("Item key and name are required.");
      return;
    }

    // Check if key already exists
    if (menuSections[showAddItemForm].items[newItemData.key]) {
      alert("This item key already exists. Please choose a different key.");
      return;
    }

    const updatedSections = {
      ...menuSections,
      [showAddItemForm]: {
        ...menuSections[showAddItemForm],
        items: {
          ...menuSections[showAddItemForm].items,
          [newItemData.key]: {
            name: newItemData.name,
            desc: newItemData.desc,
            price: newItemData.price,
            allergens: newItemData.allergens,
          },
        },
      },
    };

    setMenuSections(updatedSections);
    saveMenuSections(updatedSections);
    setShowAddItemForm(false);
  };

  const handleAllergenToggle = (allergenId) => {
    if (isEditingItem && currentItem) {
      const allergens = currentItem.allergens || [];
      let updatedAllergens;

      if (allergens.includes(Number(allergenId))) {
        updatedAllergens = allergens.filter((id) => id !== Number(allergenId));
      } else {
        updatedAllergens = [...allergens, Number(allergenId)].sort(
          (a, b) => a - b
        );
      }

      setCurrentItem({
        ...currentItem,
        allergens: updatedAllergens,
      });
    } else if (showAddItemForm) {
      const allergens = newItemData.allergens || [];
      let updatedAllergens;

      if (allergens.includes(Number(allergenId))) {
        updatedAllergens = allergens.filter((id) => id !== Number(allergenId));
      } else {
        updatedAllergens = [...allergens, Number(allergenId)].sort(
          (a, b) => a - b
        );
      }

      setNewItemData({
        ...newItemData,
        allergens: updatedAllergens,
      });
    }
  };

  // Funzione per renderizzare i prodotti per MenuCarta
  const renderProductsSection = () => {
    if (page !== "menuCarta") return null;

    const products = currentTranslations.home?.prodotti?.items || [];
    const otherProducts = otherTranslations.home?.prodotti?.items || [];

    if (!products || products.length === 0) {
      return (
        <div className="text-center p-4 bg-gray-50 rounded mb-4">
          <p>
            Nessun prodotto disponibile. Aggiungi prodotti nella sezione Home
            per visualizzarli qui.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white p-6 rounded-lg border mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Prodotti nel catalogo</h3>
          <div className="text-sm text-gray-500">
            I prodotti sono condivisi con la Homepage
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left">Nome Prodotto</th>
              <th className="p-3 text-left">Categoria</th>
              <th className="p-3 text-left">Tag</th>
              <th className="p-3 text-center">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => {
              // Determina la categoria in base ai tag
              let category = "Specialità";
              if (!product.tags) product.tags = [];

              if (
                product.tags.some(
                  (tag) =>
                    tag.toLowerCase().includes("salame") ||
                    tag.toLowerCase().includes("salsiccia") ||
                    tag.toLowerCase().includes("sopressa")
                )
              ) {
                category = "Salami";
              } else if (
                product.tags.some(
                  (tag) =>
                    tag.toLowerCase().includes("prosciutto") ||
                    tag.toLowerCase().includes("fiocco") ||
                    tag.toLowerCase().includes("bresaola")
                )
              ) {
                category = "Prosciutti e Fiocchi";
              } else if (
                product.tags.some(
                  (tag) =>
                    tag.toLowerCase().includes("pancetta") ||
                    tag.toLowerCase().includes("lardo")
                )
              ) {
                category = "Pancette e Lardi";
              }

              return (
                <tr key={index} className="border-b">
                  <td className="p-3">
                    <div className="flex items-center">
                      <div
                        className="w-10 h-10 bg-cover bg-center rounded mr-3"
                        style={{ backgroundImage: `url(${product.img})` }}
                      />
                      <div>
                        <p className="font-medium">{product.title}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {product.desc.substring(0, 60)}...
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">{category}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {product.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="bg-gray-100 px-2 py-0.5 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => {
                        // Se hai funzioni per cambiare la sezione e il modulo
                        if (
                          typeof onSectionChange === "function" &&
                          typeof onModuleChange === "function"
                        ) {
                          onSectionChange("home");
                          onModuleChange("products");
                        } else {
                          alert(
                            "Per modificare i prodotti, vai alla sezione Home e utilizza il modulo Products"
                          );
                        }
                      }}
                      className="text-blue-500 hover:text-blue-700 px-2 py-1 bg-blue-50 rounded flex items-center justify-center mx-auto gap-1"
                    >
                      <FaEdit className="text-xs" />
                      <span>Modifica in Home</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-6 bg-blue-50 p-4 rounded text-sm">
          <p className="font-medium text-blue-800 mb-2">Nota:</p>
          <p className="text-blue-700">
            Per gestire i prodotti, vai alla sezione Home e utilizza il modulo
            Products. I prodotti aggiunti nella Home saranno automaticamente
            disponibili in questa pagina.
          </p>
        </div>
      </div>
    );
  };

  return (
    <TranslationModuleBase
      title={`${page.charAt(0).toUpperCase() + page.slice(1)} Manager`}
    >
      {/* Main Menu Sections View */}
      {!isEditingSection &&
        !isEditingItem &&
        !showAddSectionForm &&
        !showAddItemForm && (
          <>
            {/* Sezione prodotti per MenuCarta */}
            {page === "menuCarta" && renderProductsSection()}

            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Menu Sections</h3>
              <button
                onClick={handleAddSection}
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center"
              >
                <FaPlus className="mr-1" /> Add Section
              </button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections" type="section">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-4"
                  >
                    {Object.keys(menuSections).length === 0 ? (
                      <div className="text-center p-4 bg-gray-50 rounded">
                        No menu sections found. Click "Add Section" to create
                        one.
                      </div>
                    ) : (
                      Object.keys(menuSections).map((sectionKey, index) => (
                        <Draggable
                          key={sectionKey}
                          draggableId={sectionKey}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="border rounded-lg overflow-hidden bg-white"
                            >
                              <div
                                {...provided.dragHandleProps}
                                className="bg-gray-50 p-3 flex justify-between items-center border-b"
                              >
                                <div className="flex items-center">
                                  <button
                                    onClick={() =>
                                      toggleSectionCollapse(sectionKey)
                                    }
                                    className="mr-2 text-gray-500"
                                  >
                                    {collapsedSections[sectionKey] ? "►" : "▼"}
                                  </button>
                                  <h3 className="font-medium">
                                    {menuSections[sectionKey].title}
                                    <span className="text-gray-400 text-sm ml-2">
                                      ({sectionKey})
                                    </span>
                                  </h3>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAddItem(sectionKey)}
                                    className="p-1 text-green-500 hover:text-green-700"
                                    title="Add item"
                                  >
                                    <FaPlus />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleEditSection(sectionKey)
                                    }
                                    className="p-1 text-blue-500 hover:text-blue-700"
                                    title="Edit section"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteSection(sectionKey)
                                    }
                                    className="p-1 text-red-500 hover:text-red-700"
                                    title="Delete section"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>

                              {!collapsedSections[sectionKey] && (
                                <div className="p-3">
                                  <Droppable
                                    droppableId={sectionKey}
                                    type="item"
                                  >
                                    {(provided) => (
                                      <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-2"
                                      >
                                        {Object.keys(
                                          menuSections[sectionKey].items || {}
                                        ).length === 0 ? (
                                          <div className="text-center p-2 bg-gray-50 rounded text-sm">
                                            No items in this section. Click + to
                                            add items.
                                          </div>
                                        ) : (
                                          Object.keys(
                                            menuSections[sectionKey].items || {}
                                          ).map((itemKey, itemIndex) => {
                                            const item =
                                              menuSections[sectionKey].items[
                                                itemKey
                                              ];
                                            return (
                                              <Draggable
                                                key={itemKey}
                                                draggableId={`${sectionKey}-${itemKey}`}
                                                index={itemIndex}
                                              >
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="p-2 border rounded flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                                                  >
                                                    <div>
                                                      <div className="font-medium">
                                                        {item.name}
                                                      </div>
                                                      <div className="text-sm text-gray-600">
                                                        {item.price}
                                                      </div>
                                                      {item.allergens &&
                                                        item.allergens.length >
                                                          0 && (
                                                          <div className="text-xs text-gray-500 mt-1">
                                                            Allergens:{" "}
                                                            {item.allergens
                                                              .map(
                                                                (id) =>
                                                                  allergens[id]
                                                              )
                                                              .join(", ")}
                                                          </div>
                                                        )}
                                                    </div>
                                                    <div className="flex space-x-1">
                                                      <button
                                                        onClick={() =>
                                                          handleEditItem(
                                                            sectionKey,
                                                            itemKey
                                                          )
                                                        }
                                                        className="p-1 text-blue-500 hover:text-blue-700"
                                                        title="Edit item"
                                                      >
                                                        <FaEdit />
                                                      </button>
                                                      <button
                                                        onClick={() =>
                                                          handleDeleteItem(
                                                            sectionKey,
                                                            itemKey
                                                          )
                                                        }
                                                        className="p-1 text-red-500 hover:text-red-700"
                                                        title="Delete item"
                                                      >
                                                        <FaTrash />
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            );
                                          })
                                        )}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}

      {/* Edit Section Form */}
      {isEditingSection && currentSection && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Edit Section</h3>
            <button
              onClick={() => {
                setIsEditingSection(false);
                setCurrentSection(null);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Section Key
              </label>
              <input
                type="text"
                value={currentSection.key}
                disabled
                className="w-full p-2 border rounded bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Section keys cannot be changed after creation.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Title ({currentLang.toUpperCase()})
              </label>
              <input
                type="text"
                value={currentSection.title}
                onChange={(e) =>
                  setCurrentSection({
                    ...currentSection,
                    title: e.target.value,
                  })
                }
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Title ({secondaryLang.toUpperCase()})
              </label>
              <input
                type="text"
                value={
                  otherTranslations[page]?.sections?.[currentSection.key]
                    ?.title || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations };
                  if (!updatedOther[page]) updatedOther[page] = {};
                  if (!updatedOther[page].sections)
                    updatedOther[page].sections = {};
                  if (!updatedOther[page].sections[currentSection.key]) {
                    updatedOther[page].sections[currentSection.key] = {
                      title: e.target.value,
                      items: {},
                    };
                  } else {
                    updatedOther[page].sections[currentSection.key] = {
                      ...updatedOther[page].sections[currentSection.key],
                      title: e.target.value,
                    };
                  }
                  onUpdateTranslations(currentTranslations, updatedOther);
                }}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => {
                setIsEditingSection(false);
                setCurrentSection(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSection}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Add Section Form */}
      {showAddSectionForm && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Add New Section</h3>
            <button
              onClick={() => setShowAddSectionForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Section Key
              </label>
              <input
                type="text"
                value={newSectionKey}
                onChange={(e) =>
                  setNewSectionKey(
                    e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "")
                  )
                }
                className="w-full p-2 border rounded"
                placeholder="e.g. appetizers (lowercase, no spaces)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use only lowercase letters and numbers, no spaces or special
                characters.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="e.g. Appetizers"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => setShowAddSectionForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={saveNewSection}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Add Section
            </button>
          </div>
        </div>
      )}

      {/* Edit Item Form */}
      {isEditingItem && currentItem && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Edit Menu Item</h3>
            <button
              onClick={() => {
                setIsEditingItem(false);
                setCurrentItem(null);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Item Key</label>
              <input
                type="text"
                value={currentItem.key}
                disabled
                className="w-full p-2 border rounded bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Item keys cannot be changed after creation.
              </p>
            </div>

            <div>
              <h4 className="font-medium mb-2">{currentLang.toUpperCase()}</h4>

              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={currentItem.name || ""}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, name: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  value={currentItem.desc || ""}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, desc: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">Price</label>
                <input
                  type="text"
                  value={currentItem.price || ""}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, price: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium mb-2">
                {secondaryLang.toUpperCase()}
              </h4>

              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={
                    otherTranslations[page]?.sections?.[currentItem.sectionKey]
                      ?.items?.[currentItem.key]?.name || ""
                  }
                  onChange={(e) => {
                    const updatedOther = { ...otherTranslations };
                    if (!updatedOther[page]) updatedOther[page] = {};
                    if (!updatedOther[page].sections)
                      updatedOther[page].sections = {};
                    if (!updatedOther[page].sections[currentItem.sectionKey]) {
                      updatedOther[page].sections[currentItem.sectionKey] = {
                        title: menuSections[currentItem.sectionKey].title,
                        items: {},
                      };
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey].items
                    ) {
                      updatedOther[page].sections[
                        currentItem.sectionKey
                      ].items = {};
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey]
                        .items[currentItem.key]
                    ) {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        name: e.target.value,
                        desc: "",
                        price: currentItem.price || "",
                        allergens: currentItem.allergens || [],
                      };
                    } else {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        ...updatedOther[page].sections[currentItem.sectionKey]
                          .items[currentItem.key],
                        name: e.target.value,
                      };
                    }
                    onUpdateTranslations(currentTranslations, updatedOther);
                  }}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  value={
                    otherTranslations[page]?.sections?.[currentItem.sectionKey]
                      ?.items?.[currentItem.key]?.desc || ""
                  }
                  onChange={(e) => {
                    const updatedOther = { ...otherTranslations };
                    if (!updatedOther[page]) updatedOther[page] = {};
                    if (!updatedOther[page].sections)
                      updatedOther[page].sections = {};
                    if (!updatedOther[page].sections[currentItem.sectionKey]) {
                      updatedOther[page].sections[currentItem.sectionKey] = {
                        title: menuSections[currentItem.sectionKey].title,
                        items: {},
                      };
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey].items
                    ) {
                      updatedOther[page].sections[
                        currentItem.sectionKey
                      ].items = {};
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey]
                        .items[currentItem.key]
                    ) {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        name: "",
                        desc: e.target.value,
                        price: currentItem.price || "",
                        allergens: currentItem.allergens || [],
                      };
                    } else {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        ...updatedOther[page].sections[currentItem.sectionKey]
                          .items[currentItem.key],
                        desc: e.target.value,
                      };
                    }
                    onUpdateTranslations(currentTranslations, updatedOther);
                  }}
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">Price</label>
                <input
                  type="text"
                  value={
                    otherTranslations[page]?.sections?.[currentItem.sectionKey]
                      ?.items?.[currentItem.key]?.price || ""
                  }
                  onChange={(e) => {
                    const updatedOther = { ...otherTranslations };
                    if (!updatedOther[page]) updatedOther[page] = {};
                    if (!updatedOther[page].sections)
                      updatedOther[page].sections = {};
                    if (!updatedOther[page].sections[currentItem.sectionKey]) {
                      updatedOther[page].sections[currentItem.sectionKey] = {
                        title: menuSections[currentItem.sectionKey].title,
                        items: {},
                      };
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey].items
                    ) {
                      updatedOther[page].sections[
                        currentItem.sectionKey
                      ].items = {};
                    }
                    if (
                      !updatedOther[page].sections[currentItem.sectionKey]
                        .items[currentItem.key]
                    ) {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        name: "",
                        desc: "",
                        price: e.target.value,
                        allergens: currentItem.allergens || [],
                      };
                    } else {
                      updatedOther[page].sections[currentItem.sectionKey].items[
                        currentItem.key
                      ] = {
                        ...updatedOther[page].sections[currentItem.sectionKey]
                          .items[currentItem.key],
                        price: e.target.value,
                      };
                    }
                    onUpdateTranslations(currentTranslations, updatedOther);
                  }}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Allergens (applies to both languages)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(allergens).map(([id, name]) => (
                  <label key={id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={(currentItem.allergens || []).includes(
                        Number(id)
                      )}
                      onChange={() => handleAllergenToggle(id)}
                      className="form-checkbox"
                    />
                    <span>{name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => {
                setIsEditingItem(false);
                setCurrentItem(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateItem}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Add Item Form */}
      {showAddItemForm && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Add New Item to {menuSections[showAddItemForm]?.title}
            </h3>
            <button
              onClick={() => setShowAddItemForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Item Key</label>
              <input
                type="text"
                value={newItemData.key}
                onChange={(e) =>
                  setNewItemData({
                    ...newItemData,
                    key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""),
                  })
                }
                className="w-full p-2 border rounded"
                placeholder="e.g. pasta_carbonara (lowercase, no spaces)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use only lowercase letters and numbers, no spaces or special
                characters.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={newItemData.name}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, name: e.target.value })
                }
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                value={newItemData.desc}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, desc: e.target.value })
                }
                className="w-full p-2 border rounded"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Price</label>
              <input
                type="text"
                value={newItemData.price}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, price: e.target.value })
                }
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Allergens
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(allergens).map(([id, name]) => (
                  <label key={id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={(newItemData.allergens || []).includes(
                        Number(id)
                      )}
                      onChange={() => handleAllergenToggle(id)}
                      className="form-checkbox"
                    />
                    <span>{name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => setShowAddItemForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={saveNewItem}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Add Item
            </button>
          </div>
        </div>
      )}

      {/* Delete Section Confirmation Modal */}
      {confirmDeleteSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-4">
              Are you sure you want to delete the section "
              {menuSections[confirmDeleteSection]?.title}"? This will also
              delete all items in this section. This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmDeleteSection(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteSectionAction}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Item Confirmation Modal */}
      {confirmDeleteItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-4">
              Are you sure you want to delete the item "
              {
                menuSections[confirmDeleteItem.sectionKey]?.items?.[
                  confirmDeleteItem.itemKey
                ]?.name
              }
              "? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setConfirmDeleteItem(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteItemAction}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </TranslationModuleBase>
  );
};

export default MenuManager;
